import { Link } from "react-router-dom";
import { Phone, ArrowRight, Home as HomeIcon, ChevronRight, Award, MapPin, Leaf, Bug, Star } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsAppButton";
import BackToTop from "../components/BackToTop";
import { Button } from "../components/ui/button";
import { useSEO } from "../lib/useSEO";
import { SITE } from "../lib/site";

const CANONICAL = "https://kreuse-exterieur.fr/a-propos-kevin-fondateur";

const JSON_LD = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "AboutPage",
      "@id": `${CANONICAL}#page`,
      url: CANONICAL,
      name: "À propos de Kevin — Fondateur K'REUSE EXTÉRIEUR",
      description:
        "Portrait de Kevin, fondateur de K'REUSE EXTÉRIEUR à La Celle-Dunoise (Creuse). Paysagiste certifié Certibiocide, spécialiste création de jardin et lutte anti-nuisibles.",
      mainEntity: {
        "@type": "Person",
        name: "Kevin",
        jobTitle: "Paysagiste et applicateur biocide certifié",
        worksFor: {
          "@type": "LocalBusiness",
          "@id": "https://kreuse-exterieur.fr/#business",
          name: "K'REUSE EXTÉRIEUR",
          address: {
            "@type": "PostalAddress",
            streetAddress: "27 rue des Pradelles",
            addressLocality: "La Celle-Dunoise",
            postalCode: "23800",
            addressRegion: "Creuse",
            addressCountry: "FR",
          },
        },
        hasCredential: [
          {
            "@type": "EducationalOccupationalCredential",
            credentialCategory: "certification",
            name: "Certibiocide",
          },
        ],
        knowsAbout: [
          "Paysagisme",
          "Création de jardin",
          "Élagage",
          "Lutte anti-nuisibles",
          "Traitement punaises de lit",
          "Destruction de nids de frelons",
        ],
      },
    },
    {
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://kreuse-exterieur.fr/" },
        { "@type": "ListItem", position: 2, name: "À propos", item: CANONICAL },
      ],
    },
  ],
};

export default function AProposKevin() {
  useSEO({
    title: "À propos de Kevin — Paysagiste Creuse | K'REUSE EXTÉRIEUR",
    description:
      "Kevin, fondateur de K'REUSE EXTÉRIEUR à La Celle-Dunoise : paysagiste et expert anti-nuisibles certifié Certibiocide en Creuse (23). 25 avis 5/5.",
    canonical: CANONICAL,
    jsonLd: JSON_LD,
  });

  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-14 md:pt-40 md:pb-20 overflow-hidden">
          <div className="absolute inset-0 -z-10 pointer-events-none">
            <div className="absolute -top-20 -left-20 w-96 h-96 rounded-full bg-[#C8E6C9] opacity-40 blur-3xl" />
          </div>
          <div className="max-w-5xl mx-auto px-6 md:px-12">
            <nav aria-label="Breadcrumb" className="flex items-center text-sm text-[#5C705E] mb-6 flex-wrap">
              <Link to="/" className="flex items-center gap-1 hover:text-[#1B5E20]">
                <HomeIcon className="w-3.5 h-3.5" />
                Accueil
              </Link>
              <ChevronRight className="w-3.5 h-3.5 mx-2" />
              <span className="text-[#1B5E20] font-medium">À propos</span>
            </nav>

            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#1B5E20]/10 text-[#1B5E20] text-xs font-semibold tracking-wide uppercase mb-6">
              <Award className="w-4 h-4" />
              Fondateur · Certifié Certibiocide
            </div>

            <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light text-[#1A2E1C] tracking-tight leading-[1.05] max-w-3xl">
              Kevin — la passion du{" "}
              <span className="font-medium text-[#1B5E20]">travail bien fait</span>
            </h1>

            <p className="mt-6 text-lg text-[#5C705E] max-w-2xl leading-relaxed">
              Fondateur de K'REUSE EXTÉRIEUR, paysagiste et applicateur biocide certifié. J'accompagne
              les particuliers et professionnels de la Creuse depuis mon atelier de La Celle-Dunoise.
            </p>

            <div className="mt-8 flex flex-wrap items-center gap-6 text-sm text-[#5C705E]">
              <div className="flex items-center gap-2">
                <Award className="w-4 h-4 text-[#1B5E20]" />
                Certifié Certibiocide
              </div>
              <div className="flex items-center gap-2">
                <Star className="w-4 h-4 fill-[#FBBC04] text-[#FBBC04]" />
                25 avis 5,0/5 Google
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-[#1B5E20]" />
                La Celle-Dunoise (23800)
              </div>
            </div>
          </div>
        </section>

        {/* Story */}
        <section className="py-14 md:py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 prose-kreuse">
            <h2>Mon parcours</h2>
            <p>
              Passionné par la nature et le travail extérieur depuis toujours, j'ai créé K'REUSE
              EXTÉRIEUR pour offrir aux habitants de la Creuse un service alliant{" "}
              <strong>expertise technique</strong> et <strong>proximité humaine</strong>.
              L'entreprise est basée à La Celle-Dunoise (23800), au cœur du département.
            </p>
            <p>
              Formé à la fois au paysagisme (création et entretien de jardins) et à la lutte
              anti-nuisibles avec la certification obligatoire <strong>Certibiocide</strong>, je peux
              intervenir sur l'ensemble des besoins extérieurs de mes clients : de l'esthétique d'un
              massif à l'éradication d'une invasion de punaises de lit.
            </p>

            <h2>Mes engagements</h2>
            <ul>
              <li>
                <strong>Devis gratuit sous 24h</strong> — clair, sans surprise, ni coût caché.
              </li>
              <li>
                <strong>Certibiocide</strong> — agrément d'État obligatoire pour l'application de
                biocides à titre professionnel. Respect strict des dosages réglementaires.
              </li>
              <li>
                <strong>Écoute et conseils</strong> — je prends le temps d'expliquer chaque
                intervention. Vous décidez en connaissance de cause.
              </li>
              <li>
                <strong>Ancrage local</strong> — installé en Creuse, je connais le climat, les
                sols, les espèces locales. Mon réseau de confiance couvre 6 départements.
              </li>
              <li>
                <strong>Éthique</strong> — je privilégie systématiquement les méthodes les moins
                invasives (vapeur sèche, gestes mécaniques) avant d'utiliser des biocides.
              </li>
            </ul>

            <h2>Mes services</h2>
            <p>K'REUSE EXTÉRIEUR intervient sur :</p>
            <ul>
              <li>
                <Link to="/paysagiste-creuse">Création de jardin sur mesure</Link>
              </li>
              <li>
                <Link to="/entretien-jardin-creuse">Entretien de jardin (éligible crédit d'impôt 50%)</Link>
              </li>
              <li>
                <Link to="/elagage-creuse">Élagage et abattage d'arbres</Link>
              </li>
              <li>
                <Link to="/entreprise-nuisibles-creuse">Lutte anti-nuisibles</Link> — frelons,
                guêpes, rongeurs
              </li>
              <li>
                <Link to="/desinsectisation-creuse">Désinsectisation</Link> — cafards, fourmis,
                mouches
              </li>
              <li>
                <Link to="/punaises-de-lit-creuse">Traitement punaises de lit</Link> — vapeur sèche
                180 °C
              </li>
              <li>
                <Link to="/desinfection-creuse">Désinfection professionnelle</Link>
              </li>
            </ul>

            <h2>Zone d'intervention</h2>
            <p>
              J'interviens dans toute la <strong>Creuse (23)</strong> et les départements
              limitrophes : Haute-Vienne (87), Indre (36), Allier (03). Voir toutes les{" "}
              <Link to="/zone-intervention-creuse-et-limitrophes">
                communes desservies
              </Link>{" "}
              ou les pages dédiées{" "}
              <Link to="/anti-nuisibles-gueret">Guéret</Link>,{" "}
              <Link to="/anti-nuisibles-aubusson">Aubusson</Link>,{" "}
              <Link to="/anti-nuisibles-la-souterraine">La Souterraine</Link>.
            </p>

            <h2>Contact direct</h2>
            <p>
              Le plus simple : appelez-moi au{" "}
              <a href="tel:+33780541115">
                <strong>07 80 54 11 15</strong>
              </a>{" "}
              ou envoyez-moi un WhatsApp avec une photo si vous avez un doute (frelon, insecte,
              nuisible). Je vous réponds sous 24h, week-ends inclus.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 bg-[#1B5E20] text-white">
          <div className="max-w-3xl mx-auto px-6 md:px-12 text-center">
            <h2 className="font-['Outfit'] text-3xl sm:text-4xl font-light tracking-tight mb-4">
              Un projet ou un problème ? Discutons.
            </h2>
            <p className="text-white/80 mb-8">Devis gratuit sous 24h — sans engagement.</p>
            <div className="flex flex-wrap justify-center gap-3">
              <a href="tel:+33780541115" data-testid="about-kevin-cta-call">
                <Button className="rounded-full bg-white text-[#1B5E20] hover:bg-white/90 px-8 h-14 text-base font-semibold">
                  <Phone className="w-4 h-4 mr-2" />
                  07 80 54 11 15
                </Button>
              </a>
              <Link to="/#contact">
                <Button variant="outline" className="rounded-full border-white bg-transparent text-white hover:bg-white hover:text-[#1B5E20] px-8 h-14 text-base">
                  Devis gratuit
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}
